import React, { Fragment, useEffect, useState, useMemo, useRef } from 'react';
import classnames from 'classnames';
import styles from './styles.scss';
import { useLocation, useParams } from 'react-router-dom';

import BackButton from '../../BackButton';
import ErrorState from '../../ErrorState';
import Loading from '../../Loading';
import { ResourceStatus } from '../../../types/resource';

import {
  MultiPointInstallationCaptureGroupActionTypes,
} from '../../../types/multi-point-installation-capture-group';
import MultiPointInstallationCaptureGroupStore from '../../../rx-stores/multi-point-installation-capture-group';
import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';
import { DEFAULT_MAX_X_OFFSET_MULTIPLIER, DEFAULT_SPAN_MS } from '../../../utils/multi-point-installations';

import SensorActionButtons from '../../Sensors/ActionButtons';

import CaptureViewer from '../Captures/CaptureViewer';

import {
  AppBar,
  AppBarSection,
  AppBarTitle,
  Button,
  Skeleton,
} from '@densityco/ui';
import ColoredPercent from '../../ColoredPercent';

const POLLING_INTERVAL_SECONDS = 5;

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

const MultiPointInstallationCaptures: React.FunctionComponent<{}> = () => {
  const state = useRxStore(MultiPointInstallationCaptureGroupStore);
  const dispatch = useRxDispatch();
  const [seek, setSeek] = useState<number | null>(null);

  const { organizationIdOrAll, mpiId, captureGroupId } = useParams();
  const query = useQuery();

  useEffect(() => {
    if (query.has("t")) {
      setSeek(parseInt(query.get("t"), 10));
    }

    dispatch({
      type: MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_REQUEST_LOAD,
      mpiId,
      captureGroupId,
    });

  }, []);

  useInterval(() => {
    dispatch({
      type: MultiPointInstallationCaptureGroupActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_GROUP_REQUEST_RELOAD,
      mpiId,
      captureGroupId,
    });
  }, POLLING_INTERVAL_SECONDS * 1000)

  const [selectedSensorIndex, setSelectedSensorIndex] = useState(0);

  switch (state.resource.status) {
    case ResourceStatus.IDLE:
    case ResourceStatus.LOADING:
      return (
        <Fragment>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              <Skeleton width={200} height={24} />
            </AppBarTitle>
          </AppBar>

          <div className={styles.wrapper}>
            <div className={styles.list}>
              <div className={styles.listInner}>
                <Loading />
              </div>
            </div>
            <div className={styles.viewer}>
              <AppBar />
              <Loading />
            </div>
          </div>
        </Fragment>
      );

    case ResourceStatus.ERROR:
      return (
        <Fragment>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              <Skeleton width={200} height={24} />
            </AppBarTitle>
          </AppBar>

          <ErrorState error={state.resource.error} />
        </Fragment>
      );

    case ResourceStatus.COMPLETE:
      const mpi = state.resource.data.mpi;

      // HACK: Unfortunately, there's an issue where sometimes MPI sensor positions start at 1, not
      // at zero. To work around this, detect if they are one based, and if they are, subtract 1
      // from each position.
      const mpiSensorsZeroBased = mpi.sensors.find(i => i.position === 0);
      if (!mpiSensorsZeroBased) {
        mpi.sensors = mpi.sensors.map(s => ({
          ...s,
          position: s.position - 1,
        }));
      }

      const captureGroup = state.resource.data.captureGroup;

      const selectedSensors = (
        mpi.sensors
          .filter(s => s.doorway)
          .map(item => ({
            ...item,
            serial_number: item.serial_number,
            link_id: item.link_id,
            doorway_name: item.doorway_name,
            organization: {
              id: item.organization.id,
              name: item.organization.name,
            },
            sensor_type: item.sensor_type,
            os_version: item.os_version,
            diagnostic_info_json: item.diagnostic_info_json,
            in_mpi: true,
          }))
      );

      const header = (
        <AppBar>
          <AppBarSection>
            <BackButton />
            <div className={styles.name}>
              {mpi.name}
              <ul className={styles.nameMetadata}>
                <li className={styles.nameMetadataItem}>
                  Total Accuracy: <strong>
                    <ColoredPercent
                      percent={
                        state.resource.data.mpiAccuracy.total_dedupe_accuracy
                      }
                    />
                  </strong>
                </li>
                <li className={styles.nameMetadataItem}>
                  Sensor Height: <strong>{mpi.sensor_height}&quot;</strong>
                </li>
                <li className={classnames(styles.nameMetadataItem, styles.extra)}>
                  Span: <strong>{mpi.config.span_ms || DEFAULT_SPAN_MS}ms</strong>
                </li>
                <li className={classnames(styles.nameMetadataItem, styles.extra)}>
                  Max X Offset Multiplier: <strong>{mpi.config.max_x_offset_multiplier || DEFAULT_MAX_X_OFFSET_MULTIPLIER}</strong>
                </li>
              </ul>
            </div>
          </AppBarSection>
          <AppBarSection>
            <SensorActionButtons
              presentation="detail"
              sensors={selectedSensors}
              primaryActionButton={
                <Button
                  variant="filled"
                  href={`#/${organizationIdOrAll}/multi-point-installations/${mpiId}/edit`}
                >
                  Edit MPI Configuration
                </Button>
              }
            />
          </AppBarSection>
        </AppBar>
      );

      if (mpi.sensors.length < 2) {
        return (
          <Fragment>
            {header}

            <div
              className={styles.centeredWrapper}
              data-test-id="mpi-captures-not-enough-sensors"
            >
              <h3>
                This MPI contains {mpi.sensors.length} sensor{mpi.sensors.length !== 1 ? 's' : ''}.
              </h3>
              <p>MPIs must contain at least two sensors to function correctly.</p>
            </div>
          </Fragment>
        );
      }

      return (
        <Fragment>
          {header}
          <div className={styles.wrapper}>
            <div className={styles.viewer}>
              <CaptureViewer
                captureGroup={captureGroup}
                mpi={mpi}
                seek={seek}
                selectedCaptureGroupId={captureGroup.id}
              />
            </div>
          </div>
        </Fragment>
      );

    default:
      return null;
  }
};

function useInterval(callback: (...args: any[]) => void, delay: number) {
  // Ref: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
  const savedCallback = useRef<(...args: any[]) => void>();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export default MultiPointInstallationCaptures;
